import React, { useEffect, useState, useRef } from 'react';
import { PlusIcon, XIcon, FolderIcon } from 'lucide-react';
import { ConditionGroup } from '../types';
interface AddConditionFormProps {
  groups: ConditionGroup[];
  preselectedGroupId?: string;
  onAdd: (data: {
    name: string;
    description: string;
    assignedTo: string;
    groupId?: string;
  }) => void;
  onCancel: () => void;
}
export function AddConditionForm({
  groups,
  preselectedGroupId,
  onAdd,
  onCancel
}: AddConditionFormProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [groupId, setGroupId] = useState<string | undefined>(preselectedGroupId);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) {
      onAdd({
        name: name.trim(),
        description: description.trim(),
        assignedTo: assignedTo.trim(),
        groupId: groupId === 'none' ? undefined : groupId
      });
    }
  };
  return <form onSubmit={handleSubmit} className="bg-slate-50 border border-slate-200 rounded-lg p-4 my-4 shadow-sm" data-id="element-21">
      <div className="flex items-center justify-between mb-4" data-id="element-22">
        <h3 className="text-sm font-medium text-slate-800 flex items-center gap-2" data-id="element-23">
          <PlusIcon className="w-4 h-4 text-blue-600" data-id="element-24" />
          Add New Condition
        </h3>
        <button type="button" onClick={onCancel} className="p-1 text-slate-400 hover:text-slate-600 transition-colors rounded hover:bg-slate-200" data-id="element-25">
          <XIcon className="w-4 h-4" data-id="element-26" />
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-27">
        <div className="col-span-1 md:col-span-2" data-id="element-28">
          <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-29">
            Condition Name *
          </label>
          <input ref={inputRef} type="text" value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Settlement Certification" className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" required data-id="element-30" />
        </div>

        <div className="col-span-1 md:col-span-2" data-id="element-31">
          <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-32">
            Description
          </label>
          <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Additional details..." rows={2} className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" data-id="element-33" />
        </div>

        <div data-id="element-34">
          <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-35">
            Assigned To
          </label>
          <input type="text" value={assignedTo} onChange={e => setAssignedTo(e.target.value)} placeholder="e.g., Rama Kacho" className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-36" />
        </div>

        <div data-id="element-37">
          <label className="block text-xs font-medium text-slate-700 mb-1" data-id="element-38">
            Group (Optional)
          </label>
          <div className="relative" data-id="element-39">
            <select value={groupId || 'none'} onChange={e => setGroupId(e.target.value === 'none' ? undefined : e.target.value)} className="w-full pl-8 pr-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white" data-id="element-40">
              <option value="none" data-id="element-41">Ungrouped</option>
              {groups.map(g => <option key={g.id} value={g.id} data-id="element-42">
                  {g.name}
                </option>)}
            </select>
            <FolderIcon className="w-4 h-4 text-slate-400 absolute left-2.5 top-2.5 pointer-events-none" data-id="element-43" />
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2 border-t border-slate-200" data-id="element-44">
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-200 rounded-md transition-colors" data-id="element-45">
          Cancel
        </button>
        <button type="submit" disabled={!name.trim()} className="px-4 py-1.5 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-id="element-46">
          Add Condition
        </button>
      </div>
    </form>;
}